import React, { useState, useEffect, useRef, useCallback } from 'react';
import Hls from 'hls.js';
import ColorThief from 'colorthief';
import { streamingAPI } from '../../services/api';
import './RadioPlayer.css';

const RadioPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [loading, setLoading] = useState(false);
  const [volume, setVolume] = useState(70);
  const [themeColors, setThemeColors] = useState({
    primary: 'rgb(138, 43, 226)',
    secondary: 'rgb(102, 126, 234)',
    accent: 'rgb(240, 147, 251)'
  });
  const audioRef = useRef(null);
  const hlsRef = useRef(null);
  const trackIdRef = useRef(null);
  const fetchIntervalRef = useRef(null);
  const playButtonRef = useRef(null);
  const volumeControlRef = useRef(null);

  // Extract colors from album art
  const extractColors = useCallback((imageUrl) => {
    if (!imageUrl) return;

    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.src = imageUrl;

    img.onload = () => {
      try {
        const colorThief = new ColorThief();
        const palette = colorThief.getPalette(img, 5);
        
        if (palette && palette.length >= 3) {
          // Convert RGB arrays to CSS rgb strings
          const colors = palette.map(rgb => `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`);
          
          setThemeColors({
            primary: colors[0],
            secondary: colors[1],
            accent: colors[2]
          });
          
          console.log('🎨 Theme colors extracted:', colors);
        }
      } catch (error) {
        console.error('Error extracting colors:', error);
      }
    };

    img.onerror = () => {
      console.warn('Failed to load image for color extraction');
    };
  }, []);

  const fetchNowPlaying = useCallback(async () => {
    try {
      const response = await streamingAPI.getNowPlaying();
      const newTrackId = response.data?.track?.id;
      const isTTS = response.data?.isTTS || false;
      
      // If TTS is playing, show it temporarily
      if (isTTS) {
        setNowPlaying(response.data);
        return;
      }
      
      // Only update state if track ID actually changed
      if (newTrackId && newTrackId !== trackIdRef.current) {
        console.log('🎵 New track detected:', response.data.track.title);
        trackIdRef.current = newTrackId;
        setNowPlaying(response.data);
        
        // Extract colors from new track's album art
        if (response.data.track.image_url) {
          extractColors(response.data.track.image_url);
        }
      } else if (response.data && trackIdRef.current === newTrackId) {
        // Update nowPlaying without triggering stream reload
        setNowPlaying(response.data);
      }
    } catch (error) {
      console.error('Error fetching now playing:', error);
    }
  }, [extractColors]);

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (fetchIntervalRef.current) {
        clearInterval(fetchIntervalRef.current);
      }
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
    };
  }, []);

  useEffect(() => {
    // Poll for now playing info every 2 seconds (matches reduced stream latency)
    fetchIntervalRef.current = setInterval(fetchNowPlaying, 2000);
    fetchNowPlaying();
    
    return () => {
      if (fetchIntervalRef.current) {
        clearInterval(fetchIntervalRef.current);
      }
    };
  }, [fetchNowPlaying]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
    }
  }, [volume]);

  const initHLS = () => {
    if (!audioRef.current) return;

    const streamUrl = '/stream.m3u8';
    
    // Check if HLS is natively supported (Safari)
    if (audioRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      console.log('📱 Native HLS support detected');
      audioRef.current.src = streamUrl;
      audioRef.current.load();
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          setLoading(false);
          fetchNowPlaying();
        })
        .catch(error => {
          console.error('Playback error:', error);
          setLoading(false);
        });
    }
    // Use hls.js for browsers that don't support HLS natively
    else if (Hls.isSupported()) {
      console.log('🎬 Using hls.js for HLS playback');
      
      // Destroy existing HLS instance if any
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
      
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 6,        // Keep minimal back buffer (6s)
        maxBufferLength: 4,          // Maximum forward buffer (4s)
        maxBufferSize: 60 * 1000 * 1000, // 60MB buffer
        maxMaxBufferLength: 6,       // Never buffer more than 6s ahead
        liveSyncDurationCount: 2,    // Stay 2 segments behind live edge
        liveMaxLatencyDurationCount: 4, // Max 4 segments behind (~4s)
        liveDurationInfinity: true,
        highBufferWatchdogPeriod: 1,
        nudgeMaxRetry: 5,
        maxFragLookUpTolerance: 0.25
      });
      
      hlsRef.current = hls;
      
      hls.loadSource(streamUrl);
      hls.attachMedia(audioRef.current);
      
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('✅ HLS manifest loaded');
        audioRef.current.play()
          .then(() => {
            setIsPlaying(true);
            setLoading(false);
            fetchNowPlaying();
          })
          .catch(error => {
            console.error('Playback error:', error);
            setLoading(false);
          });
      });
      
      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('HLS error:', data);
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.log('Network error, attempting to recover...');
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.log('Media error, attempting to recover...');
              hls.recoverMediaError();
              break;
            default:
              console.error('Fatal HLS error, auto-reconnecting stream...');
              if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
              }
              setTimeout(() => {
                initHLS();
              }, 1000);
              break;
          }
        }
      });
    } else {
      console.error('HLS is not supported in this browser');
      alert('Je browser ondersteunt geen HLS streaming. Gebruik een moderne browser.');
      setLoading(false);
    }
  };

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;

    if (playButtonRef.current) {
      playButtonRef.current.classList.add('clicked');
      setTimeout(() => {
        playButtonRef.current?.classList.remove('clicked');
      }, 800);
    }

    if (isPlaying) {
      audioRef.current.pause();
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      setIsPlaying(false);
    } else {
      setLoading(true);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      initHLS();
    }
  }, [initHLS, isPlaying]);

  const handleVolumeChange = (e) => {
    setVolume(e.target.value);
  };

  useEffect(() => {
    const mediaSession = navigator.mediaSession;
    const track = nowPlaying?.track;

    if (!mediaSession || !track) {
      if (mediaSession) {
        mediaSession.metadata = null;
        mediaSession.playbackState = 'none';
      }
      return;
    }

    const artworkUrl = track.image_url
      ? new URL(track.image_url, window.location.origin).toString()
      : null;

    try {
      mediaSession.metadata = new MediaMetadata({
        title: track.title || 'AudioCore',
        artist: track.artist || 'Live Radio',
        album: track.album || 'AudioCore',
        artwork: artworkUrl
          ? [{ src: artworkUrl, sizes: '512x512', type: 'image/jpeg' }]
          : []
      });
      mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
      mediaSession.setActionHandler('play', () => togglePlay());
      mediaSession.setActionHandler('pause', () => togglePlay());
    } catch (error) {
      console.warn('Media Session metadata not supported:', error);
    }
  }, [isPlaying, nowPlaying, togglePlay]);

  return (
    <div 
      className={`radio-player ${isPlaying ? 'music-vibing' : ''}`}
      style={{
        '--bg-image': nowPlaying?.track?.image_url 
          ? `url(${nowPlaying.track.image_url})`
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        '--theme-primary': themeColors.primary,
        '--theme-secondary': themeColors.secondary,
        '--theme-accent': themeColors.accent
      }}
    >
      <style>
        {nowPlaying?.track?.image_url && `
          .radio-player::before {
            background-image: url(${nowPlaying.track.image_url});
          }
        `}
      </style>
      
      <div className="audiocore-watermark">AudioCore</div>
      
      {isPlaying && (
        <div className="live-indicator">
          <span className="live-dot"></span>
          LIVE
        </div>
      )}
      
      <div className="player-container">

        <div className="album-art-wrapper">
          <div className="album-art">
            {nowPlaying?.track?.image_url ? (
              <img 
                src={nowPlaying.track.image_url} 
                alt="Album Art"
                onError={(e) => {
                  console.warn('Failed to load album art:', nowPlaying.track.image_url);
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="no-image">
                <i className="fas fa-music"></i>
              </div>
            )}
          </div>
        </div>

        <div className="track-info">
          {nowPlaying?.status === 'waiting' ? (
            <div className="loading-track">
              <p>Radio aan het voorbereiden...</p>
            </div>
          ) : nowPlaying?.track ? (
            <>
              <h1 className="track-title">{nowPlaying.track.title}</h1>
              <p className="track-artist">{nowPlaying.track.artist || 'Unknown Artist'}</p>
            </>
          ) : (
            <div className="loading-track">
              <p>Loading tracks...</p>
            </div>
          )}
        </div>

        <div className="player-controls">
          <button 
            ref={playButtonRef}
            className="play-button"
            onClick={togglePlay}
            disabled={loading}
          >
            {loading ? (
              <i className="fas fa-spinner fa-spin"></i>
            ) : isPlaying ? (
              <i className="fas fa-pause"></i>
            ) : (
              <i className="fas fa-play"></i>
            )}
          </button>
        </div>

        <div ref={volumeControlRef} className="volume-control">
          <i className={`volume-icon fas ${volume === 0 ? 'fa-volume-mute' : volume < 50 ? 'fa-volume-down' : 'fa-volume-up'}`}></i>
          <input
            type="range"
            min="0"
            max="100"
            value={volume}
            onChange={handleVolumeChange}
            className="volume-slider"
          />
          <span className="volume-value">{volume}%</span>
        </div>

        <audio
          ref={audioRef}
          crossOrigin="anonymous"
          onError={(e) => {
            console.error('Audio error:', e);
            console.error('Audio error details:', e.target.error);
            setIsPlaying(false);
            setLoading(false);
          }}
          onLoadStart={() => console.log('📥 Loading HLS stream...')}
          onPlaying={() => console.log('▶️ Stream playing')}
          onWaiting={() => console.log('⏳ Stream buffering...')}
        />
      </div>
    </div>
  );
};

export default RadioPlayer;
